import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import classNames from 'classnames';
import { useIntercom } from 'react-use-intercom';
import { InnerView } from './InnerView';
import { OuterView } from './OuterView';
import { accWidgetMachine } from '../machines/acceleratorMachines/accWidgetMachine';
import { EVENTS as WIDGET_EVENTS, STATES as WIDGET_STATES, TAGS as WIDGET_TAGS } from '../interfaces/widgetInterface';
import { IWidgetConfig } from '../interfaces/configInterface';
import { Loader } from './ui/Loader';
import { Error } from './Error';
import { Header } from './Header';
import { SliderModal } from './SliderModal';
import { EVENTS as RECORDER_EVENTS } from '../interfaces/recorderInterface';
import { getIPhoneModel, isIphone, isMobile } from '../utils/device.utils';
import i18n from '../i18n/config';
import { WelcomePage } from './WelcomePage';
import { MinimizeButton } from './ui/MinimizeButton';
import { isRtl } from '../utils/languages.utils';
import { EVENT_TYPES, emitTrackEvent } from '../services/event.service';
import { SentryHub } from '../utils/sentry.utils';

interface IProps {
  widgetConfig: IWidgetConfig;
  setShouldShowWaterMark: React.Dispatch<React.SetStateAction<boolean>>;
  myinterviewRef: React.RefObject<HTMLDivElement>
  isWidgetMinimized: boolean;
  onMinimize: () => void;
}
export const Main:React.FC<IProps> = ({ widgetConfig, setShouldShowWaterMark, myinterviewRef, isWidgetMinimized, onMinimize }) => {
  const { boot, shutdown, update } = useIntercom();
  const viewsRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [machine, send] = useMachine(() => accWidgetMachine.withContext(
    { ...accWidgetMachine.context, widgetConfig, videoRef },
  ));
  const [isSliderModalOpen, setIsSliderModalOpen] = useState(false);
  const { recorderRef, widgetConfig: { job, candidate, video, company, config }, error } = machine.context;
  const isErrorState = machine.matches(WIDGET_STATES.ERROR);
  const isLoading = machine.hasTag(WIDGET_TAGS.LOADING);
  const isSetupState = machine.matches(WIDGET_STATES.SETUP);
  const isUploadingState = machine.matches(WIDGET_STATES.UPLOADING);
  const isInterviewState = machine.matches(WIDGET_STATES.INTERVIEW);
  const isVideoQuestionState = machine.matches({ [WIDGET_STATES.INTERVIEW]: WIDGET_STATES.INTERVIEW__VIDEO_QUESTION });
  const isWelcomeState = machine.matches(WIDGET_STATES.WELCOME);
  useEffect(() => {
    const handleReload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Changes you made may not be saved.';
      return e;
    };

    myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-height', 'calc(40vh - 100px)');
    window.addEventListener('beforeunload', handleReload);

    if (config.hideIntercom === false) {
      boot({
        customAttributes: { is_new_widget: true },
      });
    }

    window.addEventListener('touchstart', () => false, { passive: false });

    return () => {
      window.removeEventListener('beforeunload', handleReload);
      window.removeEventListener('touchstart', () => false);
      shutdown();
    };
  }, []);

  useEffect(() => {
    i18n.changeLanguage(job?.language || 'en');
  }, [job?.language]);

  useEffect(() => {
    setShouldShowWaterMark(!!company?.shouldShowWaterMark);
  }, [company?.shouldShowWaterMark]);

  const _onFinish = async () => widgetConfig.config.onFinish?.({ redirectUrl: candidate.redirectUrl, video_id: video?.video_id || '' });

  useEffect(() => {
    if (machine.done) {
      if (widgetConfig.config.onFinish) {
        _onFinish().then(() => {
          emitTrackEvent({ eventType: EVENT_TYPES.ON_FINISH_SUCCEED });
        }).catch((err) => {
          let errorMessage = '';
          try {
            errorMessage = err?.message || err?.msg || err?.data?.message;
          } catch {
            //
          }
          emitTrackEvent({ eventType: EVENT_TYPES.ON_FINISH_FAILED, extraData: { errorMessage } });
          throw err;
        });
      }
    }
  }, [machine.done]);

  useEffect(() => {
    if (config.hideIntercom === false) {
      update({
        customAttributes: {
          apiKey: candidate?.apiKey,
          company_id: candidate?.company_id,
          name: candidate?.username,
          email: candidate?.email,
        },
        alignment: isRtl(job?.language) ? 'left' : 'right',
      });
    }
    SentryHub.configureScope((scope) => {
      scope.setTags({
        company_id: candidate?.company_id,
        email: candidate.email,
        ...(isIphone && { iphoneModel: getIPhoneModel() }),
      });
    });
  }, [candidate, job?.language]);

  const setBackgroundOpacity = () => {
    myinterviewRef?.current?.style.setProperty('--myinterview-background-opacity', isLoading || isErrorState ? '0' : '1');
  };

  useEffect(() => {
    if (isErrorState && error?.statusCode === 400) {
      widgetConfig.config.onError?.({ messageType: 'applied', messageInfo: 'Candidate already attached to a video' });
    }
    setBackgroundOpacity();
  }, [isErrorState]);

  const isResumed:boolean = useMemo(() => !!widgetConfig.video?.videos?.some((v) => v.uploaded), [widgetConfig.video]);

  const handleScroll = (e: any) => {
    const { scrollTop } = e.target;
    viewsRef?.current?.style.setProperty('--myinterview-widget-views-margin-top', `${scrollTop}px`);
    viewsRef?.current?.style.setProperty('--myinterview-widget-practice-opacity', scrollTop > 10 ? '0' : '1');
  };

  useEffect(() => {
    setBackgroundOpacity();
  }, [isLoading]);

  const onRetry = () => {
    send(WIDGET_EVENTS.RETRY);
  };

  const onRecorderRetry = () => {
    recorderRef?.send(RECORDER_EVENTS.RETRY);
    setIsSliderModalOpen(false);
  };

  const onReInitRecorder = () => {
    setIsSliderModalOpen(false);
    onRetry();
  };

  const onContinueClicked = () => {
    send(WIDGET_EVENTS.NEXT_STEP);
  };

  const viewsClassNames = classNames({
    'myinterview-widget__views': true,
    'myinterview-widget__views--uploading': isUploadingState,
    'myinterview-widget__views--rtl': isRtl(job?.language),
  });

  return (
    <>
      {!!config.minimizable && !isInterviewState && <MinimizeButton onClick={onMinimize} />}

      {!isErrorState && !isLoading && (
        <Header
          logo={job?.logo || ''}
          companyName={job?.company || company?.name || ''}
          jobTitle={job?.title || ''}
          hidden={!isVideoQuestionState && !isWelcomeState}
        />
      )}
      <div ref={viewsRef} className={viewsClassNames} onScroll={handleScroll}>
        {isErrorState
          ? <Error error={machine.context.error} onRetry={onRetry} />
          : isWelcomeState ? (
            <WelcomePage
              introVideo={widgetConfig.config.introVideo || ''}
              welcomeTitle={widgetConfig.config.welcomeTitle}
              welcomeText={widgetConfig.config.welcomeText || ''}
              onContinue={onContinueClicked}
              logo={job?.logo || ''}
              company={job?.company || company?.name || ''}
              title={job?.title || ''}
              termsUrl={job?.termsUrl}
              privacyUrl={job?.privacyUrl}
              isResumed={isResumed}
              isWidgetMinimized={isWidgetMinimized}
            />
          )
            : isLoading || !recorderRef
              ? <Loader />
              : (
                <>
                  <InnerView
                    myinterviewRef={myinterviewRef}
                    recorderRef={recorderRef}
                    ref={videoRef}
                    widgetMachine={machine}
                    sendToWidget={send}
                    isSliderModalOpen={isSliderModalOpen}
                    onOpenSliderModal={() => setIsSliderModalOpen(true)}
                    onCloseSliderModal={() => setIsSliderModalOpen(false)}
                    onRecorderRetry={onRecorderRetry}
                    onReInitRecorder={onReInitRecorder}
                    isWidgetMinimized={isWidgetMinimized}
                  />
                  <OuterView
                    widgetMachine={machine}
                    sendToWidget={send}
                    recorderRef={recorderRef}
                  />
                </>
              )}
      </div>
      {isMobile && (
      <SliderModal
        isOpen={isSliderModalOpen}
        onClose={() => setIsSliderModalOpen(false)}
        onRetry={isSetupState ? onRecorderRetry : onReInitRecorder}
      />
      )}
    </>
  );
};
